// src/utils/helpers.js

/**
 * Format currency to Indian Rupee
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Format date to readable string
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Format date and time
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted datetime string
 */
export const formatDateTime = (date) => {
  return new Date(date).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Calculate final price after discount
 * @param {number} price - Original price
 * @param {number} discount - Discount percentage
 * @returns {number} Final price
 */
export const calculateFinalPrice = (price, discount = 0) => {
  if (!discount || discount <= 0) return price;
  const discountAmount = (price * discount) / 100;
  return price - discountAmount;
};

/**
 * Calculate discount amount
 * @param {number} price - Original price
 * @param {number} discount - Discount percentage
 * @returns {number} Discount amount
 */
export const calculateDiscountAmount = (price, discount = 0) => {
  if (!discount || discount <= 0) return 0;
  return (price * discount) / 100;
};

/**
 * Calculate shipping charge
 * @param {number} subtotal - Cart subtotal
 * @param {number} freeShippingThreshold - Free shipping threshold
 * @param {number} shippingCharge - Standard shipping charge
 * @returns {number} Shipping charge
 */
export const calculateShipping = (
  subtotal,
  freeShippingThreshold = 999,
  shippingCharge = 50
) => {
  return subtotal >= freeShippingThreshold ? 0 : shippingCharge;
};

// GST is computed per item on the server (apparel: 5% ≤ ₹2,500/piece, else 18%)
// and returned as a resolved `tax` amount on cart/order responses. Prices are
// GST-inclusive, so the order total is simply subtotal + shipping — there is no
// client-side GST rate to apply here.

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} Is valid email
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone number (10 digits)
 * @param {string} phone - Phone number to validate
 * @returns {boolean} Is valid phone
 */
export const isValidPhone = (phone) => {
  const phoneRegex = /^[0-9]{10}$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
};

/**
 * Validate pincode (6 digits)
 * @param {string} pincode - Pincode to validate
 * @returns {boolean} Is valid pincode
 */
export const isValidPincode = (pincode) => {
  const pincodeRegex = /^[0-9]{6}$/;
  return pincodeRegex.test(pincode);
};

/**
 * Validate custom name for printing
 * @param {string} customName - Custom name to validate
 * @returns {boolean} Is valid custom name
 */
export const isValidCustomName = (customName) => {
  if (!customName || customName.trim() === '') return true; // Optional field
  if (customName.length > 50) return false;
  const nameRegex = /^[a-zA-Z\s]*$/;
  return nameRegex.test(customName);
};

/**
 * Truncate text to specified length
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 100) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Generate unique ID (for temporary use)
 * @returns {string} Unique ID
 */
export const generateUniqueId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
};

/**
 * Deep clone object
 * @param {Object} obj - Object to clone
 * @returns {Object} Cloned object
 */
export const deepClone = (obj) => {
  return JSON.parse(JSON.stringify(obj));
};

/**
 * Group array by key
 * @param {Array} array - Array to group
 * @param {string} key - Key to group by
 * @returns {Object} Grouped object
 */
export const groupBy = (array, key) => {
  return array.reduce((result, item) => {
    const groupKey = item[key];
    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(item);
    return result;
  }, {});
};

/**
 * Debounce function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in ms
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait = 300) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Check if object is empty
 * @param {Object} obj - Object to check
 * @returns {boolean} Is empty
 */
export const isEmpty = (obj) => {
  return Object.keys(obj).length === 0;
};

/**
 * Get error message from error object
 * @param {Error} error - Error object
 * @returns {string} Error message
 */
export const getErrorMessage = (error) => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

/**
 * Load Razorpay script
 * @returns {Promise} Promise that resolves when script is loaded
 */
export const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

/**
 * Scroll to top of page
 */
export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
};

/**
 * Get a seed number that stays stable for the whole calendar day (IST),
 * then changes at midnight. Used to rotate product order daily.
 * @returns {number} Unsigned 32-bit seed derived from today's date
 */
export const getDailySeed = () => {
  // e.g. "2026-07-21" in Asia/Kolkata
  const dateStr = new Date().toLocaleDateString('en-CA', {
    timeZone: 'Asia/Kolkata',
  });
  let hash = 0;
  for (let i = 0; i < dateStr.length; i++) {
    hash = (hash << 5) - hash + dateStr.charCodeAt(i);
    hash |= 0; // force 32-bit
  }
  return hash >>> 0;
};

/**
 * Deterministic Fisher-Yates shuffle.
 * Same seed always produces the same order, so the layout is stable
 * within a day (no flicker on re-render / re-fetch) but rotates when
 * the seed changes.
 * @param {Array} array - Array to shuffle (not mutated)
 * @param {number} seed - Seed value (defaults to today's daily seed)
 * @returns {Array} A new, shuffled array
 */
export const seededShuffle = (array, seed = getDailySeed()) => {
  const result = [...array];
  let state = seed >>> 0;
  // mulberry32 PRNG
  const rand = () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

/**
 * Get initials from name
 * @param {string} name - Full name
 * @returns {string} Initials
 */
export const getInitials = (name) => {
  if (!name) return '';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};